import { TokenList } from '@components/TokenList/TokenList'
import { useDiscoverData } from '@plug-core/hooks/useDiscoverData'
import { Link } from '@plug-core/idls/plugTokenRegistry/plugTokenRegistry.did'
import { TokenBalance } from '@plug-core/providers/BalanceProvider'
import { isSpotlightEvent } from '@providers/SpotlightProvider'
import * as RadixDialog from '@radix-ui/react-dialog'
import { Avatar, Input, ListGroup, ListRow, Text } from '@ui'
import { ROUTES } from '@utils/routes'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import React, { FC, PropsWithChildren, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { HiOutlineSearch } from 'react-icons/hi'
import { useNavigate } from 'react-router-dom'

interface SpotlightSearchProps {
  open: boolean
  onClose: () => void
}

const LinkSearchResult = ({ link, onClose }: { link: Link; onClose: () => void }) => (
  <ListRow
    dense
    inset
    onClick={() => {
      window.open(link.link, '_blank')
      onClose()
    }}
    leading={<Avatar src={link.image} alt="" size={36} fallback={link.title} />}
    primary={link.title}
    secondary={link.description[0]}
  />
)

const Title = ({ children }: PropsWithChildren) => (
  <Text variant="label" tone="fg-2" weight="semibold" className="px-2 pt-1 pb-2">
    {children}
  </Text>
)

export const SpotlightSearch: FC<SpotlightSearchProps> = ({ open, onClose }) => {
  const { search, searchTerm, searchResults } = useDiscoverData()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const reduce = useReducedMotion()

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current?.focus()
    } else {
      search('')
    }
  }, [open, inputRef.current])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape is handled by the dialog itself; this only toggles closed on the spotlight shortcut.
      if (isSpotlightEvent(e) && open) onClose()
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  const handleTokenClick = (token: TokenBalance) => {
    navigate(ROUTES.TOKEN_DETAILS(token.token.id))
    onClose()
  }

  const hasResults = Object.values(searchResults).flat().length > 0
  const showResults = searchTerm.length > 1

  return (
    <RadixDialog.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <AnimatePresence>
        {open && (
          <RadixDialog.Portal forceMount>
            <RadixDialog.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-[100] bg-scrim backdrop-blur-[4px]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              />
            </RadixDialog.Overlay>
            <div className="pointer-events-none fixed inset-x-0 top-[15%] z-[101] mx-auto w-full max-w-[560px] px-4">
              <RadixDialog.Content
                asChild
                forceMount
                onOpenAutoFocus={(e) => {
                  e.preventDefault()
                  inputRef.current?.focus()
                }}
              >
                <motion.div
                  initial={reduce ? { opacity: 0 } : { opacity: 0, y: -20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={reduce ? { opacity: 0 } : { opacity: 0, y: -20, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="plug-sheet-surface pointer-events-auto flex flex-col rounded-xl border border-hairline bg-sheet text-fg shadow-md outline-none"
                >
                  <RadixDialog.Title className="sr-only">
                    {t('discover.searchPlaceholder')}
                  </RadixDialog.Title>
                  <RadixDialog.Description className="sr-only" />

                  <div className="p-3">
                    <Input
                      ref={inputRef}
                      value={searchTerm}
                      onChange={(e) => search(e.target.value)}
                      placeholder={t('discover.searchPlaceholder')}
                      aria-label={t('discover.searchPlaceholder')}
                      leading={<HiOutlineSearch size={20} aria-hidden="true" />}
                      trailing={
                        <kbd className="rounded-xs border border-hairline bg-chip px-1.5 py-0.5 font-mono text-[11px] font-semibold text-fg-2">
                          ESC
                        </kbd>
                      }
                    />
                  </div>

                  <div className="no-scrollbar max-h-[400px] overflow-y-auto border-t border-hairline p-2">
                    {showResults && (
                      <>
                        {searchResults.tokens.length > 0 && (
                          <div className="flex flex-col">
                            <Title>{t('discover.tokens')}</Title>

                            <TokenList
                              balances={searchResults.tokens.slice(0, 5)}
                              onTokenClick={handleTokenClick}
                              showPriceChange
                            />
                          </div>
                        )}

                        {searchResults.links.length > 0 && (
                          <div className="mt-2 flex flex-col">
                            <Title>{t('discover.articles')}</Title>

                            <ListGroup card={false} className="[&>*+*]:border-t-0">
                              {searchResults.links.slice(0, 3).map((link) => (
                                <LinkSearchResult key={link.id} link={link} onClose={onClose} />
                              ))}
                            </ListGroup>
                          </div>
                        )}

                        {searchResults.dapps.length > 0 && (
                          <div className="mt-2 flex flex-col">
                            <Title>{t('discover.dapps')}</Title>

                            <ListGroup card={false} className="[&>*+*]:border-t-0">
                              {searchResults.dapps.slice(0, 3).map((dapp) => (
                                <LinkSearchResult key={dapp.id} link={dapp} onClose={onClose} />
                              ))}
                            </ListGroup>
                          </div>
                        )}

                        {!hasResults && (
                          <div className="flex items-center justify-center py-8">
                            <Text tone="fg-2">{t('discover.noResults')}</Text>
                          </div>
                        )}
                      </>
                    )}

                    {!showResults && (
                      <Text variant="body-s" tone="fg-2" className="p-2">
                        Search for tokens, dApps, and articles...
                      </Text>
                    )}
                  </div>
                </motion.div>
              </RadixDialog.Content>
            </div>
          </RadixDialog.Portal>
        )}
      </AnimatePresence>
    </RadixDialog.Root>
  )
}

export default SpotlightSearch
